/**
 * Rate confirmation acceptance — field names and payload type.
 * Used by: frontend (AvailableLoads, LoadRequestCard, NotificationsTray) and
 * backend accept flows (acceptLoadForPO, acceptLoadForBrokerPO, handleUnifiedLoadAcceptance).
 * Keep field names in sync with Firestore load/PO documents.
 */

/** Firestore field: timestamp when carrier confirmed rate confirmation (ISO string or Timestamp). */
export const RATE_CONFIRMATION_ACCEPTED_AT = 'rateConfirmationAcceptedAt';

/** Firestore field: carrier uid who confirmed. */
export const RATE_CONFIRMATION_ACCEPTED_BY = 'rateConfirmationAcceptedBy';

/** Firestore field: optional signature name from modal. */
export const RATE_CONFIRMATION_SIGNATURE_NAME = 'rateConfirmationSignatureName';

/** Phase 8A: Immutable JSON of modal payload + acceptance at sign time (for signed PDF). */
export const RATE_CONFIRMATION_SIGNED_SNAPSHOT = 'rateConfirmationSignedSnapshot';

/** Phase 8A: Firebase Storage path to signed PDF (set by Cloud Function in Phase 8C). */
export const RATE_CONFIRMATION_PDF_STORAGE_PATH = 'rateConfirmationPdfStoragePath';

/** Phase 8A: PDF generation lifecycle for in-app download. */
export const RATE_CONFIRMATION_PDF_STATUS = 'rateConfirmationPdfStatus';

/** Phase 8C: Server timestamp when signed PDF was stored. */
export const RATE_CONFIRMATION_PDF_GENERATED_AT = 'rateConfirmationPdfGeneratedAt';

/** Phase 8C: Last PDF generation error (optional). */
export const RATE_CONFIRMATION_PDF_ERROR = 'rateConfirmationPdfError';

/** {@link RATE_CONFIRMATION_PDF_STATUS} values — align with functions/src/rateConfirmationConstants.ts */
export const RATE_CONFIRMATION_PDF_STATUS_PENDING = 'pending' as const;
export const RATE_CONFIRMATION_PDF_STATUS_READY = 'ready' as const;
export const RATE_CONFIRMATION_PDF_STATUS_FAILED = 'failed' as const;

export type RateConfirmationPdfStatus =
  | typeof RATE_CONFIRMATION_PDF_STATUS_PENDING
  | typeof RATE_CONFIRMATION_PDF_STATUS_READY
  | typeof RATE_CONFIRMATION_PDF_STATUS_FAILED;

/** Canonical PDF file name — align with functions/src/rateConfirmationConstants.ts */
export const RATE_CONFIRMATION_PDF_ARTIFACT_FILE_NAME = 'signed.pdf';

/** Storage prefix for signed rate confirmation PDFs — align with functions/src/rateConfirmationConstants.ts */
export const RATE_CONFIRMATION_PDF_STORAGE_PREFIX = 'rateConfirmationDocs';

/** Signed URL TTL for in-app download — align with functions/src/rateConfirmationConstants.ts */
export const RATE_CONFIRMATION_SIGNED_URL_TTL_MS = 30 * 60 * 1000;

/** Cloud Function names — align with functions exports (Phase 8D). */
export const RATE_CONFIRMATION_DOWNLOAD_CALLABLE = 'getRateConfirmationDownloadUrl';
export const RATE_CONFIRMATION_REGENERATE_CALLABLE = 'regenerateRateConfirmationPdf';

/** Carrier-facing copy when server PDF render fails — align with functions/src/rateConfirmationConstants.ts */
export const RATE_CONFIRMATION_PDF_USER_RETRY_MESSAGE =
  'PDF generation failed. Retry in a moment.';

/** Callable failed-precondition when PDF status is not pending/failed/ready — align with functions. */
export const RATE_CONFIRMATION_PDF_REGENERATE_DENIED_MESSAGE =
  'PDF cannot be regenerated for this document state.';

export type RateConfirmationDownloadResult =
  | { status: typeof RATE_CONFIRMATION_PDF_STATUS_PENDING }
  | { status: typeof RATE_CONFIRMATION_PDF_STATUS_READY; url: string; fileName: string }
  | { status: typeof RATE_CONFIRMATION_PDF_STATUS_FAILED; error?: string };

export interface RateConfirmationAcceptancePayload {
  acceptedAt: string;
  acceptedBy: string;
  signatureName?: string;
}

/** Frozen at carrier confirm — source of truth for signed PDF (Phase 8A). */
export interface RateConfirmationSignedSnapshot {
  payload: import('../utils/rateConfirmationPayload').RateConfirmationPayload;
  acceptance: RateConfirmationAcceptancePayload;
}

/**
 * Firestore field: assigned carrier object after accept / RC complete.
 * Align with functions/src/rateConfirmationConstants.ts and carrier assignment validation.
 */
export const APPROVED_CARRIER = 'approvedCarrier';

/** PO field: when set, shipper/broker approved carrier but carrier must confirm rate confirmation before assign (Phase 4B). */
export const APPROVED_CARRIER_PENDING_RATE_CONFIRMATION = 'approvedCarrierPendingRateConfirmation';

/** Nested on {@link APPROVED_CARRIER_PENDING_RATE_CONFIRMATION} when shipper/broker accepted a counter rate. */
export const PENDING_RATE_CONFIRMATION_AGREED_RATE = 'agreedRate';

/**
 * Shipper/broker original marketplace listing rate (immutable anchor).
 * Set before agreed rate overwrites rate/amount/total; restored when carrier declines after accept.
 * Align: backend/src/utils/marketplaceListedRate.ts, functions/src/rateConfirmationConstants.ts
 */
export const MARKETPLACE_LISTED_RATE = 'listedRate';

/** Nested on pending snapshot — copy of {@link MARKETPLACE_LISTED_RATE} at accept time. */
export const PENDING_RATE_CONFIRMATION_LISTED_RATE = 'listedRate';

/** Snapshot stored on PO while carrier must sign rate confirmation (Phase 1 deferred commit). */
export interface PendingCarrierRateConfirmationSnapshot {
  id: string;
  companyName: string;
  email: string;
  phoneNumber?: string;
  mcNumber?: string;
  dotNumber?: string;
  agreedRate?: number;
  listedRate?: number;
}

/**
 * Phase 5.1 / 6: Backward compatibility: Loads/POs accepted before this feature do not have rateConfirmationAcceptedAt.
 * Use this helper when displaying "Rate confirmation accepted at/by" so missing = null (treat as "accepted before feature" or hide).
 * Phase 6: Also returns signatureName when present (optional audit line). acceptedAt may be string or Firestore Timestamp.
 */
export function getRateConfirmationDisplay(
  record: Record<string, unknown> | null | undefined
): { acceptedAt: string; acceptedBy: string; signatureName?: string } | null {
  if (!record || typeof record !== 'object') return null;
  const at = record[RATE_CONFIRMATION_ACCEPTED_AT];
  const by = record[RATE_CONFIRMATION_ACCEPTED_BY];
  if (at == null || by == null || typeof by !== 'string') return null;
  const atStr =
    typeof at === 'string'
      ? at
      : typeof (at as { toDate?: () => Date })?.toDate === 'function'
        ? (at as { toDate: () => Date }).toDate().toISOString()
        : null;
  if (!atStr) return null;
  const sig = record[RATE_CONFIRMATION_SIGNATURE_NAME];
  const signatureName = typeof sig === 'string' && sig.trim() ? sig.trim() : undefined;
  return { acceptedAt: atStr, acceptedBy: by, signatureName };
}

/** Prefer load field, then PO — matches Cloud Functions record resolver (Phase 8D). */
export function pickRateConfirmationField(
  load: Record<string, unknown> | null | undefined,
  po: Record<string, unknown> | null | undefined,
  field: string
): unknown {
  if (load && load[field] != null) return load[field];
  if (po && po[field] != null) return po[field];
  return undefined;
}

/**
 * PDF lifecycle for UI (Phase 8E). Returns null when rate confirmation was never accepted.
 * When accepted but status missing (legacy), treats as pending.
 */
export function resolveRateConfirmationPdfStatus(
  load: Record<string, unknown> | null | undefined,
  po: Record<string, unknown> | null | undefined
): RateConfirmationPdfStatus | null {
  const hasAcceptance = getRateConfirmationDisplay(load) || getRateConfirmationDisplay(po);
  if (!hasAcceptance) return null;

  const value = pickRateConfirmationField(load, po, RATE_CONFIRMATION_PDF_STATUS);
  if (
    value === RATE_CONFIRMATION_PDF_STATUS_PENDING ||
    value === RATE_CONFIRMATION_PDF_STATUS_READY ||
    value === RATE_CONFIRMATION_PDF_STATUS_FAILED
  ) {
    return value;
  }
  return RATE_CONFIRMATION_PDF_STATUS_PENDING;
}

/** DOM id / hash target for Load Details Documents section (Phase 8E). */
export const RATE_CONFIRMATION_DOCUMENTS_SECTION_ID = 'rate-confirmation-documents';

/** Load Details section heading — signed RC lives here (not a separate My Loads row action). */
export const RATE_CONFIRMATION_DOCUMENTS_SECTION_TITLE = 'Signed Rate Confirmation';

/** After this duration in `pending`, carrier may retry PDF generation (align functions constant). */
export const RATE_CONFIRMATION_PDF_PENDING_STALE_MS = 3 * 60 * 1000;

/** True when acceptance is recorded but PDF has stayed pending longer than {@link RATE_CONFIRMATION_PDF_PENDING_STALE_MS}. */
export function isRateConfirmationPdfPendingStale(
  load: Record<string, unknown> | null | undefined,
  po: Record<string, unknown> | null | undefined,
  nowMs: number = Date.now()
): boolean {
  const display = getRateConfirmationDisplay(load) || getRateConfirmationDisplay(po);
  if (!display) return false;
  const acceptedMs = Date.parse(display.acceptedAt);
  if (Number.isNaN(acceptedMs)) return false;
  return nowMs - acceptedMs >= RATE_CONFIRMATION_PDF_PENDING_STALE_MS;
}

/**
 * Firestore field keys guarded by rateConfirmationCreateValid / rateConfirmationUpdateValid (Phase 8G).
 * Keep aligned with firestore.rules and functions/src/rateConfirmationConstants.ts.
 */
export const RATE_CONFIRMATION_FIRESTORE_GUARDED_FIELD_KEYS = [
  RATE_CONFIRMATION_ACCEPTED_AT,
  RATE_CONFIRMATION_ACCEPTED_BY,
  RATE_CONFIRMATION_SIGNATURE_NAME,
  RATE_CONFIRMATION_SIGNED_SNAPSHOT,
  RATE_CONFIRMATION_PDF_STORAGE_PATH,
  RATE_CONFIRMATION_PDF_STATUS,
  RATE_CONFIRMATION_PDF_GENERATED_AT,
  RATE_CONFIRMATION_PDF_ERROR,
] as const;
